import he from 'he';
import fp from 'lodash/fp';
import { matchPath } from 'react-router';
import { CpdCategoryName } from '../constants';
import { branchFuncs, unfixedFloor, unfixedCeil } from '../utils';

/**
 * Predicate for 'cpdCategorys'.
 */
export const findCpdCategory = (cpdCategoryId) =>
  fp.find(({ id }) => id === cpdCategoryId);

/**
 * Map function for 'userCpdCategorys', to be used with progress-(cards/charts).
 */
export const mapUserCpdCategorys = (cpdCategorys) =>
  fp.flow(
    fp.map(({
      cpdCategoryId,
      cpdCategoryDetail,
      ...userCpdCategory
    }) => ({
      ...userCpdCategory,
      cpdCategoryDetail: cpdCategoryDetail ? cpdCategoryDetail : {
        cpdCategoryId: cpdCategoryId,
        userId: null,
        totalCpdPoints: 0
      },
      cpdCategoryId,
      cpdCategory: findCpdCategory(cpdCategoryId)(attachFullName(cpdCategorys))
    })),
    fp.sortBy(({ cpdCategory }) => cpdCategory ? cpdCategory.order : 0),
    fp.map(({
      cpdCategory,
      cpdCategoryId,
      cpdCategoryDetail,
      progressPercentage,
      cappedProgressPercentage,
      validCpdPoints,
      cappedValidCpdPoints,
      shortFall,
      pendingCpdPoints,
      tasaCpdPoints,
      riskRating
    }) => ({
      cpdCategoryId,
      userId: cpdCategoryDetail ? cpdCategoryDetail.userId : null,
      title: cpdCategory ?
        he.decode(cpdCategory.name) :
        'Unknown',
      name: cpdCategory ? he.decode(cpdCategory.originName) : 'Unknown',
      progressPercentage: progressPercentage,
      cappedProgressPercentage: cappedProgressPercentage,
      totalCpdPoints: unfixedFloor(cpdCategoryDetail ? cpdCategoryDetail.totalCpdPoints : 0, 2),
      currentCpdPoints: validCpdPoints,
      cappedCurrentCpdPoints: cappedValidCpdPoints,
      remainingCpdPoints: unfixedCeil((cpdCategoryDetail ? cpdCategoryDetail.totalCpdPoints : 0) - cappedValidCpdPoints, 2),
      shortFall,
      pendingCpdPoints,
      tasaCpdPoints,
      riskRating
    }))
  );

/**
* Map function for 'userCpdCategoryProgress', to be used with progress-(cards/charts).
*/
export const mapUserCpdCategoryProgress = (cpdCategorys) =>
  fp.flow(
    ({ userId, cpdCategorys: nestedUserCpdCategory = [] }) => fp.map(
      (aoa) => ({
        ...aoa,
        userId
      })
    )(nestedUserCpdCategory.concat(
      fp.flatten(
        fp.map(
          ({ children }) => children
        )(nestedUserCpdCategory)
      )
    )),
    fp.map(({
      cpdCategoryId,
      isExternal,
      userId,
      primaryStat,
      derivedStat,
      children
    }) => ({
      ...primaryStat,
      ...derivedStat,
      children,
      cpdCategoryDetail: {
        cpdCategoryId: cpdCategoryId,
        userId: isExternal ? null : userId,
        totalCpdPoints: primaryStat.goal
      },
      cpdCategoryId: cpdCategoryId,
      cpdCategory: findCpdCategory(cpdCategoryId)(attachFullName(cpdCategorys))
    })),
    fp.sortBy(({ cpdCategory }) => cpdCategory ? cpdCategory.order : 0),
    fp.map(({
      cpdCategory,
      cpdCategoryId,
      cpdCategoryDetail,
      progress,
      validCpd,
      shortFall,
      pendingCpd,
      tasaCpd,
      riskRating,
      goal,
      children
    }) => ({
      cpdCategoryId,
      userId: cpdCategoryDetail ? cpdCategoryDetail.userId : null,
      title: cpdCategory ?
        he.decode(cpdCategory.name) :
        'Unknown',
      name: cpdCategory ? he.decode(cpdCategory.originName) : 'Unknown',
      progress,
      goal,
      validCpd,
      shortFall,
      pendingCpd,
      tasaCpd,
      riskRating,
      hasChild: children.length > 0,
      importance: goal > 0 ? 1 : 0
    }))
  );

/**
* Flatten CpdCategories with children, to be used to get 'userSelectedCpdCategory'.
*/
export const mapCpdCategoryProgress = (cpdCategorys) =>
  fp.flow(
    fp.map(aoa => aoa.children),
    fp.flatten,
    fp.concat(cpdCategorys)
  )(cpdCategorys)

export const getUserIndustryCpdCategorys = (profile, industriesWithCpdCategorys) => {
  const userIndustryIds = fp.flow(
    fp.get('trainingPlans'),
    fp.map('industry.id')
  )(profile)
  const userIndustryCpdCategorys = fp.flow(
    fp.filter(
      ({ industry }) => fp.includes(industry.id)(userIndustryIds)
    ),
    fp.flatMap('cpdCategorys')
  )(industriesWithCpdCategorys)
  return userIndustryCpdCategorys
}

export const getUserIndustryGoals = (profile, industriesWithCpdCategorys) => {
  const userIndustryIds = fp.flow(
    fp.get('trainingPlans'),
    fp.map('industry.id')
  )(profile)
  const userIndustryGoals = fp.flow(
    fp.filter(
      ({ industry }) => fp.includes(industry.id)(userIndustryIds)
    ),
    fp.flatMap('goals')
  )(industriesWithCpdCategorys)
  return userIndustryGoals
}

/**
 * Match the 'cpdCategory' path for the current location.
 */
export const matchCpdCategory = fp.curry(
  ({ pathname }, path) =>
    matchPath(pathname, {
      path: path.cpdCategory(':cpdCategory')
    })
);

/**
 * Find the 'userCpdCategory' for the matched path.
 */
export const findUserCpdCategory = fp.curry(
  (match, cpdCategorys, userCpdCategory) =>
    match ?
      fp.flow(
        mapUserCpdCategoryProgress(cpdCategorys),
        fp.find(({ cpdCategoryId }) =>
          cpdCategoryId === match
        )
      )(userCpdCategory) :
      null
);

/**
 * Find the 'userCpdCategory' for the current location.
 */
export const findMatchedUserCpdCategory = (match) =>
  findUserCpdCategory(match);

/**
* The sum of all CPD hours currently assigned to Areas of Advice.
*/
export const cpdPointsSum = fp.flow(
  fp.map(fp.get('totalCpdPoints')),
  fp.sum
);

/**
 * The number of CPD hours assigned to a given Area of Advice.
 */
export const cpdPointsForCpdCategory = (cpdCategoryId) =>
  fp.flow(
    fp.find(
      fp.flow(
        fp.get('cpdCategoryId'),
        fp.eq(cpdCategoryId)
      )
    ),
    branchFuncs(
      fp.identity,
      fp.get('totalCpdPoints'),
      () => 0
    )
  );

/*
* Find the 'cpdCategory' for the matched path.
*/
export const findMatchedCpdCategory = (match, cpdCategorys) =>
  match ?
    fp.find(({ id }) =>
      id === match
    )(cpdCategorys) :
    null

/**
 * attach full name for aoa
 */
export const attachFullName = (cpdCategorys) =>
  fp.map(
    (aoa) => {
      const parent = fp.find(({ id }) => id === aoa.parentCpdCategoryId)(cpdCategorys)
      const children = fp.find(({ parentCpdCategoryId }) => parentCpdCategoryId === aoa.id)(cpdCategorys)
      const fullName = he.decode(parent ? `${parent.name} - ${aoa.name}` : aoa.name)
      return {
        ...aoa,
        name: fullName,
        originName: aoa.name,
        order: parent ? 2 : children ? 0 : 1
      }
    }
  )(cpdCategorys)

/**
 * attach full name for aoa and remove parent aoa
 */
export const attachFullNameAndRemoveParent = (cpdCategorys) => fp.flow(
  attachFullName,
  // fp.filter(
  //   ({ id }) => fp.filter(
  //     ({ parentCpdCategoryId }) => parentCpdCategoryId === id
  //   )(cpdCategorys).length === 0
  // )
)(cpdCategorys)

/**
 * Find all children of a provided area of advice
 */
export const findSubCpdCategorys = (cpdCategoryId, industryCpdCategorys) => fp.filter(({ parentCpdCategoryId }) => parentCpdCategoryId === cpdCategoryId)(industryCpdCategorys)

export const defaultIndustryCpdCategorySorting = (industryCpdCategory) => fp.sortBy([
  ({ name }) => name !== CpdCategoryName.genecalCpd,
  ({ minimumCpd }) => minimumCpd === 0,
  ({ name }) => name === CpdCategoryName.responsibleManager,
  ({ mandatory }) => !mandatory,
  ({ id }) => fp.flow(
    fp.map(
      ({ parentCpdCategoryId }) => parentCpdCategoryId
    ),
    fp.contains(id)
  )(industryCpdCategory),
  ({ name }) => name
])(industryCpdCategory)

export const trainingPlanSortingAndGrouping = (industryCpdCategory) => {
  const parents = fp.filter(
    ({ parentCpdCategoryId }) => fp.isNil(parentCpdCategoryId)
  )(industryCpdCategory)
  const parentsWithChildren = fp.map(
    (parent) => {
      const children = fp.filter(
        ({ parentCpdCategoryId }) => parentCpdCategoryId === parent.id
      )(industryCpdCategory)
      return {
        parent,
        children: children,
      }
    }
  )(parents)
  const sorted = fp.flow(
    fp.sortBy(
      [
        ({ children }) => children.length,
        ({ parent }) => !parent.mandatory,
        ({ parent }) => parent.name
      ]
    ),
    fp.map(
      ({ parent, children }) => ({
        parent,
        children: fp.sortBy(
          [
            ({ mandatory }) => !mandatory,
            ({ name }) => name
          ]
        )(children)
      })
    )
  )(parentsWithChildren)
  return sorted
}

export const mapCpdCategoryIdsWithChildren = (aoas) => fp.flow(
  fp.filter(
    ({ selected }) => selected
  ),
  fp.map(
    ({ id }) => [id].concat(fp.flow(
      fp.filter(
        ({ parentCpdCategoryId }) => parentCpdCategoryId === id
      ),
      fp.map(
        ({ id }) => id
      )
    )(aoas))
  ),
  fp.flatten,
  fp.join(',')
)(aoas)

export const mapSelectedCpdCategoryId = (aoas) => fp.flow(
  fp.keys,
  fp.filter(
    (id) => aoas[id]
  ),
  fp.join(',')
)(aoas)

export const tileCpdCategoryClassName = (category) => category.length > 48 ? 'cat aoa-font-size-sm' : category.length > 30 ? 'cat aoa-font-size-md' : 'cat';

export const responsibleManagerAdded = (trainingPlan, prevTrainingPlan, industryCpdCategorys) => {
  const responsibleManagerCategoryId = fp.flow(
    fp.find(
      ({ name }) => name === CpdCategoryName.responsibleManager
    ),
    fp.get('id')
  )(industryCpdCategorys)
  const responsibleManagerSelected = (plan) => fp.find(
    ({ cpdCategoryId }) => cpdCategoryId === responsibleManagerCategoryId
  )(plan)
  return responsibleManagerSelected(trainingPlan) && !responsibleManagerSelected(prevTrainingPlan)
}

export const getIndustriesForCpdCategory = (cpdCategoryId, industriesWithCpdCategorys) => {
  return fp.filter(
    ({ cpdCategorys }) => {
      const cpdCategoryIds = fp.map('id')(cpdCategorys)
      return fp.contains(cpdCategoryId)(cpdCategoryIds)
    }
  )(industriesWithCpdCategorys)
}

export const getIndustryLabelForCpdCategoryNoParenthesis = (cpdCategoryId, industriesWithCpdCategorys) => {
  return fp.flow(
    fp.map(
      ({ industry }) => he.decode(industry.name)
    ),
    (industryNames) => {
      if (industryNames.length > 0) {
        return `${industryNames.join(',')}`
      } else {
        return ''
      }
    }
  )(getIndustriesForCpdCategory(cpdCategoryId, industriesWithCpdCategorys))
}

export const getIndustryLabelForCpdCategory = (cpdCategoryId, industriesWithCpdCategorys) => {
  return fp.flow(
    fp.map(
      ({ industry }) => he.decode(industry.name)
    ),
    (industryNames) => {
      if (industryNames.length > 0) {
        return `(${industryNames.join(',')})`
      } else {
        return ''
      }
    }
  )(getIndustriesForCpdCategory(cpdCategoryId, industriesWithCpdCategorys))
}


// WEBPACK FOOTER //
// ./src/services/areas-of-advice.js